import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import './FillerTask.css';

/**
 * Filler Task Component
 * Brief cognitive reset task between email and device blocks
 * Reduces mental set carry-over effects
 */
const FillerTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData } = useExperiment();
  const { tracker } = useTracker();
  
  const [currentProblem, setCurrentProblem] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [userAnswer, setUserAnswer] = useState('');
  const taskStartTime = useRef(Date.now());
  const problemStartTime = useRef(Date.now());
  
  // Simple arithmetic problems for cognitive reset (reduced to 2 for brevity)
  const problems = [
    { a: 7, b: 8, op: '+' },
    { a: 15, b: 7, op: '-' }
  ];
  
  useEffect(() => {
    const taskAssignment = experimentData?.participant?.taskAssignment;
    const afterTask = taskAssignment?.taskOrder === 'email-first'
      ? 'email'
      : taskAssignment?.cohort === 'email-device'
        ? 'device'
        : 'coding';

    tracker.track('fillerTaskStart', {
      afterTask,
      timestamp: taskStartTime.current
    });
  }, [tracker, experimentData]);
  
  const calculateAnswer = (problem: typeof problems[0]): number => {
    switch (problem.op) {
      case '+': return problem.a + problem.b;
      case '-': return problem.a - problem.b;
      case '*': return problem.a * problem.b;
      case '/': return problem.a / problem.b;
      default: return 0;
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const problem = problems[currentProblem];
    const correctAnswer = calculateAnswer(problem);
    const parsedAnswer = parseInt(userAnswer);
    const isCorrect = parsedAnswer === correctAnswer;
    const responseTime = Date.now() - problemStartTime.current;
    
    tracker.track('fillerResponse', {
      problemIndex: currentProblem,
      problem: `${problem.a} ${problem.op} ${problem.b}`,
      correctAnswer,
      userAnswer: parsedAnswer,
      correct: isCorrect,
      responseTime,
      timestamp: Date.now()
    });
    
    const nextAnswers = [...answers, parsedAnswer];
    setAnswers(nextAnswers);
    setUserAnswer('');
    
    if (currentProblem < problems.length - 1) {
      setCurrentProblem(currentProblem + 1);
      problemStartTime.current = Date.now();
    } else {
      completeFillerTask(nextAnswers);
    }
  };
  
  const completeFillerTask = (finalAnswers: number[]) => {
    tracker.track('fillerTaskComplete', {
      duration: Date.now() - taskStartTime.current,
      correctAnswers: finalAnswers.filter((ans, idx) =>
        ans === calculateAnswer(problems[idx])
      ).length,
      totalProblems: problems.length,
      timestamp: Date.now()
    });
    
    // Navigate based on two-cohort system
    let parsedSession = null;
    try {
      const sessionData = localStorage.getItem('experimentSession');
      parsedSession = sessionData ? JSON.parse(sessionData) : null;
    } catch (e) {
      console.warn('Failed to parse experimentSession from localStorage:', e);
    }
    const taskAssignment = experimentData?.participant?.taskAssignment || parsedSession?.participant?.taskAssignment;
    const cohort = taskAssignment?.cohort;
    const taskOrder = taskAssignment?.taskOrder;
    
    console.log('FillerTask two-cohort navigation:', { cohort, taskOrder, taskAssignment });
    
    if (cohort === 'email-device') {
      if (taskOrder === 'email-first') {
        navigate('/device-task-intro'); // Email done, show device intro
      } else {
        navigate('/email-task-intro'); // Device done, show email intro
      }
    } else if (cohort === 'email-coding') {
      if (taskOrder === 'email-first') {
        navigate('/coding-task-intro'); // Email done, show coding intro
      } else {
        navigate('/email-task-intro'); // Coding done, show email intro
      }
    } else {
      // Default fallback
      console.warn('No cohort assignment found, defaulting to device task intro');
      navigate('/device-task-intro');
    }
  };
  
  const problem = problems[currentProblem];
  
  return (
    <div className="filler-task">
      <div className="filler-content">
        <h2>Quick Break</h2>
        <p>Please solve these simple math problems before continuing to the next task.</p>
        
        <div className="problem-container">
          <div className="problem-number">
            Problem {currentProblem + 1} of {problems.length}
          </div>
          
          <div className="math-problem">
            <span className="operand">{problem.a}</span>
            <span className="operator">{problem.op}</span>
            <span className="operand">{problem.b}</span>
            <span className="equals">=</span>
          </div>
          
          <form onSubmit={handleSubmit}>
            <input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Your answer"
              autoFocus
              required
              className="answer-input"
            />
            
            <button type="submit" className="btn btn-primary">
              {currentProblem < problems.length - 1 ? 'Next' : 'Continue to Next Task'}
            </button>
          </form>
        </div>
        
        <div className="progress-indicator">
          <div 
            className="progress-bar"
            style={{ width: `${((currentProblem + 1) / problems.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default FillerTask;
